import {
  addDisplayName,
  addEffect,
  addProps,
  addState,
  addStateHandlers,
  addWrapper,
  flowMax,
  SimpleUnchangedProps,
} from 'ad-hok'
import { FC } from 'react'
import { typedAs } from 'shared/src/utils/typedAs'
import { createUseStyles } from 'react-jss'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'
import { addApi } from 'shared/src/api'
import { CreatorLaunchImageWithImage } from 'shared/src/api/types'

import { Modal } from '../components/Modal'
import backgroundImage from '../assets/menu-background.jpg'
import { IconButton } from '../components/IconButton'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#AE9A75',
    backgroundImage: `url(${backgroundImage})`,
    position: 'relative',
    paddingTop: 48,
    height: '100dvh',
  },
  closeButton: {
    position: 'absolute',
    top: 6,
    right: 7,
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    paddingBottom: 48,
  },
  image: {
    width: '100%',
  },
})

interface LaunchImageModalProps {
  launchImage: CreatorLaunchImageWithImage
  onClose: () => void
}

const LaunchImageModal: FC<LaunchImageModalProps> = flowMax(
  addDisplayName('LaunchImageModal'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    onClose,
    launchImage: {
      image: { full: imageUrl },
    },
    classes,
  }) => (
    <Modal containerClassName={classes.container}>
      <div className={classes.imageContainer}>
        <img src={imageUrl} alt="" className={classes.image} />
      </div>
      <IconButton
        onClick={onClose}
        name="close"
        className={classes.closeButton}
        addHitSlop
      />
    </Modal>
  )
)

const LOCAL_STORAGE_LAST_SHOWN_LAUNCH_IMAGE_ID_KEY = 'lastShownLaunchImageId'
const getLastShownLaunchImageId = (): number | null => {
  const stringValue = localStorage.getItem(
    LOCAL_STORAGE_LAST_SHOWN_LAUNCH_IMAGE_ID_KEY
  )
  if (!stringValue) return null
  return parseInt(stringValue)
}

const setLastShownLaunchImageId = (launchImageId: number) => {
  localStorage.setItem(
    LOCAL_STORAGE_LAST_SHOWN_LAUNCH_IMAGE_ID_KEY,
    launchImageId.toString()
  )
}

export const addLaunchImageShowing: SimpleUnchangedProps = flowMax(
  addStateHandlers(
    {
      showingLaunchImage: typedAs<CreatorLaunchImageWithImage | null>(null),
      lastShownLaunchImageId: getLastShownLaunchImageId(),
    },
    {
      showLaunchImageModal:
        () => (launchImage: CreatorLaunchImageWithImage) => ({
          showingLaunchImage: launchImage,
          lastShownLaunchImageId: launchImage.id,
        }),
      closeLaunchImageModal: () => () => ({
        showingLaunchImage: null,
      }),
    }
  ),
  addEffect(
    ({ lastShownLaunchImageId }) =>
      () => {
        if (!lastShownLaunchImageId) return

        setLastShownLaunchImageId(lastShownLaunchImageId)
      },
    ['lastShownLaunchImageId']
  ),
  addState(
    'latestLaunchImage',
    'setLatestLaunchImage',
    typedAs<CreatorLaunchImageWithImage | null>(null)
  ),
  addApi,
  addDataLoadingFromPromise(
    'latestLaunchImageData',
    ({ api }) => api.creatorLaunchImages.latest(),
    []
  ),
  addProps(({ latestLaunchImageData }) => ({
    latestLaunchImage: hasData(latestLaunchImageData)
      ? latestLaunchImageData.data
      : null,
  })),
  addEffect(
    ({ latestLaunchImage, lastShownLaunchImageId, showLaunchImageModal }) =>
      () => {
        if (!latestLaunchImage) return
        if (
          !(
            !lastShownLaunchImageId ||
            latestLaunchImage.id > lastShownLaunchImageId
          )
        )
          return

        showLaunchImageModal(latestLaunchImage)
      },
    ['latestLaunchImage.id']
  ),
  addWrapper((render, { showingLaunchImage, closeLaunchImageModal }) => (
    <>
      {showingLaunchImage && (
        <LaunchImageModal
          launchImage={showingLaunchImage}
          onClose={closeLaunchImageModal}
        />
      )}
      {render()}
    </>
  ))
)
